// @ts-nocheck
import { Fragment, useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useLazyQuery, useQuery } from '@apollo/client'
import { InView } from 'react-intersection-observer'
import { ChevronDown, Users } from 'lucide-react'
import { CubeLoader } from '@/components/ui/cube-loader'
import {
    GET_FEEDS,
    GET_FEED_PLATFORMS,
    GET_FEED_CATEGORIES,
    GET_CAMPAIGN_CATEGORIES,
} from '@/graphql/publisher-dashboard-queries'
import { logos } from '@/utils/platform-logos'
import FeedCard from '@/components/feed/FeedCard'
import Masonry from '@/components/feed/Masonry'
import ResponsiveMasonry from '@/components/feed/ResponsiveMasonry'
import _ from 'lodash'

const Communities = () => {
    const { id } = useParams()
    const [platform, setPlatform] = useState(null)
    const [audiences, setAudiences] = useState(null)
    const [category, setCategory] = useState(null)
    const [heights, setHeights] = useState({})
    const [isFullyLoaded, setFullyLoaded] = useState(false)
    const [isCommunityOpen, setIsCommunityOpen] = useState(false)
    const [isPlatformOpen, setIsPlatformOpen] = useState(false)

    const communityDropdownRef = useRef(null)
    const platformDropdownRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (communityDropdownRef.current && !communityDropdownRef.current.contains(e.target)) {
                setIsCommunityOpen(false)
            }
            if (platformDropdownRef.current && !platformDropdownRef.current.contains(e.target)) {
                setIsPlatformOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const [getCategories, categories] = useLazyQuery(GET_FEED_CATEGORIES)

    const campaign = useQuery(GET_CAMPAIGN_CATEGORIES, {
        variables: id ? { where: { id } } : {},
        onCompleted: (data) => {
            const campaignCategories = data.campaigns?.[0]?.categories ? JSON.parse(data.campaigns[0].categories) : []
            const aliases = _.map(campaignCategories, (cat) => cat.toLowerCase())
            getCategories({ variables: { where: { alias_IN: aliases } } })
        },
    })

    const platforms = useQuery(GET_FEED_PLATFORMS, {
        variables: { options: { sort: [{ order: 'ASC' }] }, where: { status: 'ACTIVE' } },
        onCompleted: (data) => {
            const first = data.platforms[0]
            setPlatform(first)
            getFeeds({
                variables: {
                    where: { platform: { id: first.id }, type: null },
                    options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
                },
            })
        },
    })

    const [getFeeds, feeds] = useLazyQuery(GET_FEEDS)

    const buildWhere = (p, cat) => ({
        platform: { id: p?.id },
        type: null,
        ...(cat?.id ? { profile: { categories_SINGLE: { id: cat.id } } } : {}),
    })

    const onChangePlatform = (p) => {
        setFullyLoaded(false)
        setPlatform(p)
        setIsPlatformOpen(false)
        getFeeds({
            variables: {
                where: buildWhere(p, category),
                options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
            },
        })
    }

    const onChangeCategory = (cat) => {
        setFullyLoaded(false)
        setCategory(cat)
        setIsCommunityOpen(false)
        getFeeds({
            variables: {
                where: buildWhere(platform, cat),
                options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
            },
        })
    }

    const onLoadMore = async () => {
        const result = await feeds.fetchMore({
            variables: {
                options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: feeds.data?.feeds.length },
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev
                return { ...prev, feeds: [...prev.feeds, ...fetchMoreResult.feeds] }
            },
        })
        setFullyLoaded(!result.data.feeds.length)
    }

    const handleHeightChange = (index, height) => {
        setHeights((prev) => ({ ...prev, [index]: height }))
    }

    useEffect(() => {
        if (categories?.data?.categories && !categories.loading) {
            setAudiences(_.map(categories.data.categories, (cat) => ({ id: cat.id, name: cat.name })))
        }
    }, [categories])

    if (platforms.loading || categories.loading) return <CubeLoader size="default" fullScreen={true} />
    if (platforms.error) {
        return (
            <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-24 tw:text-gray-400">
                <Users size={48} className="tw:mb-3 tw:opacity-30" />
                <p className="tw:text-sm">Failed to load communities. Please try again later.</p>
            </div>
        )
    }

    return (
        <Fragment>
            <div className="tw:mb-4">
                <div className="card page-card">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap page-card-header gap-2">
                        <h5 className="mb-0 tw:flex tw:items-center tw:gap-2">
                            <i className="icon text-gray-200">
                                <Users size={24} />
                            </i>
                            {category ? category.name : 'All Communities'} — {platform?.name}
                            <button className="sm social-btn bg-white rounded-circle p-1 no-pointer tw:border-0">
                                <img
                                    src={logos[platform?.name]}
                                    alt={platform?.name}
                                    className="img-fluid object-contain"
                                    style={{ width: '20px', height: '20px' }}
                                />
                            </button>
                        </h5>
                        <div className="tw:flex tw:flex-row tw:gap-2">
                            <div className="tw:relative" ref={communityDropdownRef}>
                                <button
                                    className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-blue-300 tw:bg-transparent tw:border-0 tw:cursor-pointer"
                                    onClick={() => setIsCommunityOpen((prev) => !prev)}
                                >
                                    Select Community <ChevronDown size={16} />
                                </button>
                                {isCommunityOpen && (
                                    <div className="tw:absolute tw:top-full tw:left-0 tw:mt-1 tw:bg-[#0a1929] tw:border tw:border-[#1e3a5f] tw:rounded tw:shadow-lg tw:z-50 tw:min-w-40">
                                        {audiences?.map((item) => (
                                            <button
                                                key={item.id}
                                                onClick={() => onChangeCategory(item)}
                                                className="tw:flex tw:w-full tw:px-3 tw:py-2 tw:text-sm tw:text-white tw:bg-transparent tw:border-0 hover:tw:bg-[#1e3a5f] tw:cursor-pointer"
                                            >
                                                {item.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="tw:relative" ref={platformDropdownRef}>
                                <button
                                    className="tw:flex tw:items-center tw:gap-1 tw:text-sm tw:text-blue-300 tw:bg-transparent tw:border-0 tw:cursor-pointer"
                                    onClick={() => setIsPlatformOpen((prev) => !prev)}
                                >
                                    Select Platform <ChevronDown size={16} />
                                </button>
                                {isPlatformOpen && (
                                    <div className="tw:absolute tw:top-full tw:left-0 tw:mt-1 tw:bg-[#0a1929] tw:border tw:border-[#1e3a5f] tw:rounded tw:shadow-lg tw:z-50 tw:min-w-32">
                                        {platforms.data?.platforms.map((item) => (
                                            <button
                                                key={item.id}
                                                onClick={() => onChangePlatform(item)}
                                                className="tw:flex tw:items-center tw:gap-2 tw:w-full tw:px-3 tw:py-2 tw:text-sm tw:text-left tw:bg-transparent tw:border-0 tw:text-white hover:tw:bg-[#1e3a5f] tw:cursor-pointer"
                                            >
                                                <span
                                                    className="bg-white rounded-circle d-inline-flex align-items-center justify-content-center"
                                                    style={{ width: '20px', height: '20px', flexShrink: 0 }}
                                                >
                                                    <img
                                                        src={logos[item.name]}
                                                        alt={item.name}
                                                        className="img-fluid object-contain"
                                                        style={{ width: '14px', height: '14px' }}
                                                    />
                                                </span>
                                                {item.name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="card-body page-card-body">
                        {feeds.loading && <CubeLoader size="default" className="tw:min-h-[300px]" />}
                        {!feeds?.loading && feeds.data && feeds.data.feeds?.length === 0 && (
                            <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:min-h-[500px] tw:text-gray-400">
                                <Users size={48} className="tw:mb-3 tw:opacity-30" />
                                <p className="tw:text-sm">No community data available for this platform.</p>
                            </div>
                        )}
                        {!feeds?.loading && feeds.data && feeds.data.feeds?.length > 0 && (
                            <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 900: 3, 1200: 4 }}>
                                <Masonry gutter="20px" heights={heights}>
                                    {feeds.data.feeds.map((feed) => (
                                        <FeedCard
                                            key={feed.id}
                                            data={feed}
                                            platform={platform}
                                            index={feed.id}
                                            onHeightChange={handleHeightChange}
                                        />
                                    ))}
                                    <InView
                                        onChange={(inView) => {
                                            if (inView) onLoadMore()
                                        }}
                                    />
                                </Masonry>
                            </ResponsiveMasonry>
                        )}
                        {!isFullyLoaded && feeds.data && feeds.data.feeds?.length > 0 && (
                            <CubeLoader size="default" fullScreen={true} />
                        )}
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default Communities
